import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, booleanAttribute, numberAttribute, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, Input, Output, ContentChildren, HostListener, NgModule } from '@angular/core';
import { Header, PrimeTemplate, SharedModule } from 'primeng/api';
import { DomHandler } from 'primeng/dom';
import { ChevronDownIcon } from 'primeng/icons/chevrondown';
import { ChevronRightIcon } from 'primeng/icons/chevronright';
import { UniqueComponentId } from 'primeng/utils';

/**
 * AccordionTab is a helper component for Accordion.
 * @group Components
 */
const _c0 = [[["p-header"]], "*"];
const _c1 = ["p-header", "*"];
const _c2 = a0 => ({
  transitionParams: a0
});
const _c3 = a0 => ({
  value: "visible",
  params: a0
});
const _c4 = a0 => ({
  value: "hidden",
  params: a0
});
function AccordionTab_span_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 9);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.header, " ");
  }
}
function AccordionTab_ng_container_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function AccordionTab_ng_content_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 0, ["*ngIf", "hasHeaderFacet"]);
  }
}
function AccordionTab_div_8_ng_container_2_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function AccordionTab_div_8_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, AccordionTab_div_8_ng_container_2_ng_container_1_Template, 1, 0, "ng-container", 5);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentTemplate);
  }
}
function AccordionTab_div_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵtemplate(2, AccordionTab_div_8_ng_container_2_Template, 2, 1, "ng-container", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r0.contentStyleClass)("ngStyle", ctx_r0.contentStyle);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r0.contentTemplate && (ctx_r0.cache ? ctx_r0.loaded : ctx_r0.selected));
  }
}
const _c5 = ["*"];
let AccordionTab = /*#__PURE__*/(() => {
  class AccordionTab {
    el;
    changeDetector;
    /**
     * Current id state as a string.
     * @group Props
     */
    id;
    /**
     * Used to define the header of the tab.
     * @group Props
     */
    header;
    /**
     * Inline style of the tab header.
     * @group Props
     */
    headerStyle;
    /**
     * Inline style of the tab.
     * @group Props
     */
    tabStyle;
    /**
     * Inline style of the tab content.
     * @group Props
     */
    contentStyle;
    /**
     * Style class of the tab.
     * @group Props
     */
    tabStyleClass;
    /**
     * Style class of the tab header.
     * @group Props
     */
    headerStyleClass;
    /**
     * Style class of the tab content.
     * @group Props
     */
    contentStyleClass;
    /**
     * Whether the tab is disabled.
     * @group Props
     */
    disabled;
    /**
     * Whether a lazy loaded panel should avoid getting loaded again on reselection.
     * @group Props
     */
    cache = true;
    /**
     * Transition options of the animation.
     * @group Props
     */
    transitionOptions = '400ms cubic-bezier(0.86, 0, 0.07, 1)';
    /**
     * Position of the icon.
     * @group Props
     */
    iconPos = 'start';
    /**
     * The value that returns the selection.
     * @group Props
     */
    get selected() {
      return this._selected;
    }
    set selected(val) {
      this._selected = val;
      if (!this.loaded) {
        if (this._selected && this.cache) {
          this.loaded = true;
        }
        this.changeDetector.detectChanges();
      }
    }
    /**
     * The aria-level that each accordion header will have. The default value is 2 as per W3C specifications
     * @group Props
     */
    headerAriaLevel = 2;
    /**
     * Event triggered by changing the choice.
     * @param {boolean} value - Boolean value indicates that the option is changed.
     * @group Emits
     */
    selectedChange = new EventEmitter();
    headerFacet;
    templates;
    _selected = false;
    get iconClass() {
      if (this.iconPos === 'end') {
        return 'p-accordion-toggle-icon-end';
      } else {
        return 'p-accordion-toggle-icon';
      }
    }
    contentTemplate;
    headerTemplate;
    iconTemplate;
    loaded = false;
    accordion;
    constructor(accordion, el, changeDetector) {
      this.el = el;
      this.changeDetector = changeDetector;
      this.accordion = accordion;
      this.id = UniqueComponentId();
    }
    ngAfterContentInit() {
      this.templates.forEach(item => {
        switch (item.getType()) {
          case 'content':
            this.contentTemplate = item.template;
            break;
          case 'header':
            this.headerTemplate = item.template;
            break;
          case 'icon':
            this.iconTemplate = item.template;
            break;
          default:
            this.contentTemplate = item.template;
            break;
        }
      });
    }
    toggle(event) {
      if (this.disabled) {
        return false;
      }
      let index = this.findTabIndex();
      if (this.selected) {
        this.selected = false;
        this.accordion.onClose.emit({
          originalEvent: event,
          index: index
        });
      } else {
        if (!this.accordion.multiple) {
          for (var i = 0; i < this.accordion.tabs.length; i++) {
            if (this.accordion.tabs[i].selected) {
              this.accordion.tabs[i].selected = false;
              this.accordion.tabs[i].selectedChange.emit(false);
              this.accordion.tabs[i].changeDetector.markForCheck();
            }
          }
        }
        this.selected = true;
        this.loaded = true;
        this.accordion.onOpen.emit({
          originalEvent: event,
          index: index
        });
      }
      this.selectedChange.emit(this.selected);
      this.accordion.updateActiveIndex();
      this.changeDetector.markForCheck();
      event?.preventDefault();
    }
    findTabIndex() {
      let index = -1;
      for (var i = 0; i < this.accordion.tabs.length; i++) {
        if (this.accordion.tabs[i] == this) {
          index = i;
          break;
        }
      }
      return index;
    }
    get hasHeaderFacet() {
      return this.headerFacet && this.headerFacet.length > 0;
    }
    onKeydown(event) {
      switch (event.code) {
        case 'Enter':
        case 'Space':
          this.toggle(event);
          event.preventDefault();
          break;
        // HIS-PATCH #266927: Barrierefreiheit -> Navigation mit Pfeiltasten links/rechts zwischen den Akkordiontabs (Elemente sollen wie Registerkarten bedient werden)
        case 'ArrowRight':
          let tabIndexRight = this.findTabIndex();
          let nextTab = tabIndexRight + 1;
          if (nextTab < this.accordion.tabs.length) {
            console.log("NextTab: ", this.accordion.tabs);
            document.getElementById(this.accordion.tabs[nextTab].id + "_header_action").focus();
          }
          break;
        case 'ArrowLeft':
          let tabIndexLeft = this.findTabIndex();
          let previousTab = tabIndexLeft - 1;
          if (previousTab >= 0) {
            document.getElementById(this.accordion.tabs[previousTab].id + "_header_action").focus();
          }
          break;
        default:
          break;
      }
    }
    getTabHeaderActionId(tabId) {
      return `${tabId}_header_action`;
    }
    getTabContentId(tabId) {
      return `${tabId}_content`;
    }
    ngOnDestroy() {
      this.accordion.tabs.splice(this.findTabIndex(), 1);
    }
    static ɵfac = function AccordionTab_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AccordionTab)(i0.ɵɵdirectiveInject(forwardRef(() => Accordion)), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: AccordionTab,
      selectors: [["p-accordionTab"]],
      contentQueries: function AccordionTab_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, Header, 4);
          i0.ɵɵcontentQuery(dirIndex, PrimeTemplate, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.headerFacet = _t);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.templates = _t);
        }
      },
      hostAttrs: [1, "p-element"],
      inputs: {
        id: "id",
        header: "header",
        headerStyle: "headerStyle",
        tabStyle: "tabStyle",
        contentStyle: "contentStyle",
        tabStyleClass: "tabStyleClass",
        headerStyleClass: "headerStyleClass",
        contentStyleClass: "contentStyleClass",
        disabled: [2, "disabled", "disabled", booleanAttribute],
        cache: [2, "cache", "cache", booleanAttribute],
        transitionOptions: "transitionOptions",
        iconPos: "iconPos",
        selected: "selected",
        headerAriaLevel: [2, "headerAriaLevel", "headerAriaLevel", numberAttribute]
      },
      outputs: {
        selectedChange: "selectedChange"
      },
      standalone: false,
      features: [i0.ɵɵInputTransformsFeature],
      ngContentSelectors: _c1,
      decls: 9,
      vars: 38,
      consts: [[1, "p-accordion-tab", 3, "ngClass", "ngStyle"], [1, "p-accordion-header"], ["role", "tab", 1, "p-accordion-header-link", 3, "click", "keydown", "ngClass", "ngStyle"], [3, "ngClass"], ["class", "p-accordion-header-text", 4, "ngIf"], [4, "ngTemplateOutlet"], [4, "ngIf"], ["role", "region", 1, "p-toggleable-content"], ["class", "p-accordion-content", 3, "ngClass", "ngStyle", 4, "ngIf"], [1, "p-accordion-header-text"], [1, "p-accordion-content", 3, "ngClass", "ngStyle"]],
      template: function AccordionTab_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c0);
          i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "a", 2);
          i0.ɵɵlistener("click", function AccordionTab_Template_a_click_2_listener($event) {
            return ctx.toggle($event);
          })("keydown", function AccordionTab_Template_a_keydown_2_listener($event) {
            return ctx.onKeydown($event);
          });
          i0.ɵɵelement(3, "span", 3);
          i0.ɵɵtemplate(4, AccordionTab_span_4_Template, 2, 1, "span", 4)(5, AccordionTab_ng_container_5_Template, 1, 0, "ng-container", 5)(6, AccordionTab_ng_content_6_Template, 1, 0, "ng-content", 6);
          i0.ɵɵelementEnd()();
          i0.ɵɵelementStart(7, "div", 7);
          i0.ɵɵtemplate(8, AccordionTab_div_8_Template, 3, 3, "div", 8);
          i0.ɵɵelementEnd()();
        }
        if (rf & 2) {
          i0.ɵɵclassProp("p-accordion-tab-active", ctx.selected);
          i0.ɵɵproperty("ngClass", ctx.tabStyleClass)("ngStyle", ctx.tabStyle);
          i0.ɵɵattribute("data-pc-name", "accordiontab");
          i0.ɵɵadvance();
          i0.ɵɵclassProp("p-highlight", ctx.selected)("p-disabled", ctx.disabled);
          i0.ɵɵattribute("data-p-disabled", ctx.disabled)("data-pc-section", "header");
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngClass", ctx.headerStyleClass)("ngStyle", ctx.headerStyle);
          i0.ɵɵattribute("tabindex", ctx.disabled ? null : 0)("id", ctx.getTabHeaderActionId(ctx.id))("aria-controls", ctx.getTabContentId(ctx.id))("aria-expanded", ctx.selected)("data-pc-section", "headeraction");
          i0.ɵɵadvance();
          i0.ɵɵclassMap(ctx.iconClass);
          i0.ɵɵproperty("ngClass", ctx.selected ? ctx.accordion.collapseIcon : ctx.accordion.expandIcon);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", !ctx.hasHeaderFacet);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngTemplateOutlet", ctx.headerTemplate);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.hasHeaderFacet);
          i0.ɵɵadvance();
          i0.ɵɵproperty("@tabContent", ctx.selected ? i0.ɵɵpureFunction1(32, _c3, i0.ɵɵpureFunction1(30, _c2, ctx.transitionOptions)) : i0.ɵɵpureFunction1(36, _c4, i0.ɵɵpureFunction1(34, _c2, ctx.transitionOptions)));
          i0.ɵɵattribute("id", ctx.getTabContentId(ctx.id))("aria-hidden", !ctx.selected)("aria-labelledby", ctx.getTabHeaderActionId(ctx.id))("data-pc-section", "toggleablecontent");
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.selected);
        }
      },
      dependencies: [i1.NgClass, i1.NgIf, i1.NgTemplateOutlet, i1.NgStyle],
      styles: [".p-accordion-header-link{cursor:pointer;display:flex;align-items:center;user-select:none;position:relative;text-decoration:none}.p-accordion-header-link:focus{z-index:1}.p-accordion-header-text{line-height:1}.p-accordion .p-toggleable-content{overflow:hidden}.p-accordion .p-accordion-tab-active>.p-toggleable-content:not(.ng-animating){overflow:inherit}.p-accordion-toggle-icon-end{order:1;margin-left:auto}.p-accordion-toggle-icon{order:0}\n"],
      encapsulation: 2,
      data: {
        animation: [trigger('tabContent', [state('hidden', style({
          height: '0',
          visibility: 'hidden'
        })), state('visible', style({
          height: '*',
          visibility: 'visible'
        })), transition('visible <=> hidden', [animate('{{transitionParams}}')]), transition('void => *', animate(0))])]
      },
      changeDetection: 0
    });
  }
  return AccordionTab;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * Accordion groups a collection of contents in tabs.
 * @group Components
 */
let Accordion = /*#__PURE__*/(() => {
  class Accordion {
    el;
    changeDetector;
    /**
     * When enabled, multiple tabs can be activated at the same time.
     * @group Props
     */
    multiple = false;
    /**
     * Inline style of the tab header and content.
     * @group Props
     */
    style;
    /**
     * Class of the element.
     * @group Props
     */
    styleClass;
    /**
     * Icon of a collapsed tab.
     * @group Props
     */
    expandIcon = 'pi pi-fw pi-chevron-right';
    /**
     * Icon of an expanded tab.
     * @group Props
     */
    collapseIcon = 'pi pi-fw pi-chevron-down';
    /**
     * Index of the active tab or an array of indexes in multiple mode.
     * @group Props
     */
    get activeIndex() {
      return this._activeIndex;
    }
    set activeIndex(val) {
      this._activeIndex = val;
      if (this.preventActiveIndexPropagation) {
        this.preventActiveIndexPropagation = false;
        return;
      }
      this.updateSelectionState();
    }
    /**
     * When enabled, the focused tab is activated.
     * @group Props
     */
    selectOnFocus = false;
    /**
     * The aria-level that each accordion header will have. The default value is 2 as per W3C specifications
     * @group Props
     */
    get headerAriaLevel() {
      return this._headerAriaLevel;
    }
    set headerAriaLevel(val) {
      if (typeof val === 'number' && val > 0) {
        this._headerAriaLevel = val;
      } else if (this._headerAriaLevel !== 2) {
        this._headerAriaLevel = 2;
      }
    }
    /**
     * Callback to invoke when an active tab is collapsed by clicking on the header.
     * @param {AccordionTabCloseEvent} event - Custom tab close event.
     * @group Emits
     */
    onClose = new EventEmitter();
    /**
     * Callback to invoke when a tab gets expanded.
     * @param {AccordionTabOpenEvent} event - Custom tab open event.
     * @group Emits
     */
    onOpen = new EventEmitter();
    /**
     * Returns the active index.
     * @param {number | number[]} value - New index.
     * @group Emits
     */
    activeIndexChange = new EventEmitter();
    tabList;
    tabListSubscription = null;
    _activeIndex;
    _headerAriaLevel = 2;
    preventActiveIndexPropagation = false;
    tabs = [];
    constructor(el, changeDetector) {
      this.el = el;
      this.changeDetector = changeDetector;
    }
    onKeydown(event) {
      switch (event.code) {
        case 'ArrowDown':
          this.onTabArrowDownKey(event);
          break;
        case 'ArrowUp':
          this.onTabArrowUpKey(event);
          break;
        case 'Home':
          if (!event.shiftKey) {
            this.onTabHomeKey(event);
          }
          break;
        case 'End':
          if (!event.shiftKey) {
            this.onTabEndKey(event);
          }
          break;
      }
    }
    focusedElementIsAccordionHeader() {
      return document.activeElement.tagName.toLowerCase() === 'a' && document.activeElement.classList.contains('p-accordion-header-link');
    }
    onTabArrowDownKey(event) {
      if (this.focusedElementIsAccordionHeader()) {
        const nextHeaderAction = this.findNextHeaderAction(event.target.parentElement.parentElement.parentElement);
        nextHeaderAction ? this.changeFocusedTab(nextHeaderAction) : this.onTabHomeKey(event);
        event.preventDefault();
      }
    }
    onTabArrowUpKey(event) {
      if (this.focusedElementIsAccordionHeader()) {
        const prevHeaderAction = this.findPrevHeaderAction(event.target.parentElement.parentElement.parentElement);
        prevHeaderAction ? this.changeFocusedTab(prevHeaderAction) : this.onTabEndKey(event);
        event.preventDefault();
      }
    }
    onTabHomeKey(event) {
      const firstHeaderAction = this.findFirstHeaderAction();
      this.changeFocusedTab(firstHeaderAction);
      event.preventDefault();
    }
    changeFocusedTab(element) {
      if (element) {
        DomHandler.focus(element);
        if (this.selectOnFocus) {
          this.tabs.forEach((tab, i) => {
            let selected = this.multiple ? this._activeIndex.includes(i) : i === this._activeIndex;
            if (this.multiple) {
              if (!this._activeIndex) {
                this._activeIndex = [];
              }
              if (tab.id == element.id) {
                tab.selected = !tab.selected;
                if (!this._activeIndex.includes(i)) {
                  this._activeIndex.push(i);
                } else {
                  this._activeIndex = this._activeIndex.filter(ind => ind !== i);
                }
              }
            } else {
              if (tab.id == element.id) {
                tab.selected = !tab.selected;
                this._activeIndex = i;
              } else {
                tab.selected = false;
              }
            }
            tab.selectedChange.emit(selected);
            this.activeIndexChange.emit(this._activeIndex);
            tab.changeDetector.markForCheck();
          });
        }
      }
    }
    findNextHeaderAction(tabElement, selfCheck = false) {
      const nextTabElement = selfCheck ? tabElement : tabElement.nextElementSibling;
      const headerElement = DomHandler.findSingle(nextTabElement, '[data-pc-section="header"]');
      return headerElement ? DomHandler.getAttribute(headerElement, 'data-p-disabled') ? this.findNextHeaderAction(headerElement.parentElement.parentElement) : DomHandler.findSingle(headerElement, '[data-pc-section="headeraction"]') : null;
    }
    findPrevHeaderAction(tabElement, selfCheck = false) {
      const prevTabElement = selfCheck ? tabElement : tabElement.previousElementSibling;
      const headerElement = DomHandler.findSingle(prevTabElement, '[data-pc-section="header"]');
      return headerElement ? DomHandler.getAttribute(headerElement, 'data-p-disabled') ? this.findPrevHeaderAction(headerElement.parentElement.parentElement) : DomHandler.findSingle(headerElement, '[data-pc-section="headeraction"]') : null;
    }
    findFirstHeaderAction() {
      const firstEl = this.el.nativeElement.firstElementChild.childNodes[0];
      return this.findNextHeaderAction(firstEl, true);
    }
    findLastHeaderAction() {
      const childNodes = this.el.nativeElement.firstElementChild.childNodes;
      const lastEl = childNodes[childNodes.length - 1];
      return this.findPrevHeaderAction(lastEl, true);
    }
    onTabEndKey(event) {
      const lastHeaderAction = this.findLastHeaderAction();
      this.changeFocusedTab(lastHeaderAction);
      event.preventDefault();
    }
    ngAfterContentInit() {
      this.initTabs();
      this.tabListSubscription = this.tabList.changes.subscribe(_ => {
        this.initTabs();
      });
    }
    initTabs() {
      this.tabs = this.tabList.toArray();
      this.tabs.forEach(tab => {
        tab.headerAriaLevel = this._headerAriaLevel;
      });
      this.updateSelectionState();
      this.changeDetector.markForCheck();
    }
    getBlockableElement() {
      return this.el.nativeElement.children[0];
    }
    updateSelectionState() {
      if (this.tabs && this.tabs.length && this._activeIndex != null) {
        for (let i = 0; i < this.tabs.length; i++) {
          let selected = this.multiple ? this._activeIndex.includes(i) : i === this._activeIndex;
          let changed = selected !== this.tabs[i].selected;
          if (changed) {
            this.tabs[i].selected = selected;
            this.tabs[i].selectedChange.emit(selected);
            this.tabs[i].changeDetector.markForCheck();
          }
        }
      }
    }
    isTabActive(index) {
      return this.multiple ? this._activeIndex && this._activeIndex.includes(index) : this._activeIndex === index;
    }
    getTabProp(tab, name) {
      return tab.props ? tab.props[name] : undefined;
    }
    updateActiveIndex() {
      let index = this.multiple ? [] : null;
      this.tabs.forEach((tab, i) => {
        if (tab.selected) {
          if (this.multiple) {
            index.push(i);
          } else {
            index = i;
            return;
          }
        }
      });
      this.preventActiveIndexPropagation = true;
      this._activeIndex = index;
      this.activeIndexChange.emit(index);
    }
    ngOnDestroy() {
      if (this.tabListSubscription) {
        this.tabListSubscription.unsubscribe();
      }
    }
    static ɵfac = function Accordion_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || Accordion)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: Accordion,
      selectors: [["p-accordion"]],
      contentQueries: function Accordion_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, AccordionTab, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.tabList = _t);
        }
      },
      hostAttrs: [1, "p-element"],
      hostBindings: function Accordion_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("keydown", function Accordion_keydown_HostBindingHandler($event) {
            return ctx.onKeydown($event);
          });
        }
      },
      inputs: {
        multiple: [2, "multiple", "multiple", booleanAttribute],
        style: "style",
        styleClass: "styleClass",
        expandIcon: "expandIcon",
        collapseIcon: "collapseIcon",
        activeIndex: "activeIndex",
        selectOnFocus: [2, "selectOnFocus", "selectOnFocus", booleanAttribute],
        headerAriaLevel: "headerAriaLevel"
      },
      outputs: {
        onClose: "onClose",
        onOpen: "onOpen",
        activeIndexChange: "activeIndexChange"
      },
      standalone: false,
      features: [i0.ɵɵInputTransformsFeature],
      ngContentSelectors: _c5,
      decls: 2,
      vars: 4,
      consts: [["role", "tablist", 3, "ngClass", "ngStyle"]],
      template: function Accordion_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵprojection(1);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.styleClass);
          i0.ɵɵproperty("ngClass", "p-accordion p-component")("ngStyle", ctx.style);
        }
      },
      dependencies: [i1.NgClass, i1.NgStyle],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return Accordion;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AccordionModule = /*#__PURE__*/(() => {
  class AccordionModule {
    static ɵfac = function AccordionModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AccordionModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: AccordionModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, ChevronRightIcon, ChevronDownIcon, SharedModule]
    });
  }
  return AccordionModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { Accordion, AccordionModule, AccordionTab };
